
import React, { useState, useEffect, useRef, useCallback } from 'react';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardTitle, Col, Container, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { VideoProcessorAPI } from '../../helpers/firebase_helper';
import ReactPlayer from 'react-player';
import { uploadData } from '@aws-amplify/storage';
import { getCurrentUser } from 'aws-amplify/auth';
// Import dashjs to ensure it's available for DASH playback
import dashjs from 'dashjs';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { languageMap } from './VideoDetails/constants';
import { srtToVtt, extractS3PathFromUrl } from './VideoDetails/captionUtils';
import { generateIframeCode } from './VideoDetails/embeddedCodeGenerator';
import { createUpdateMetrics } from './VideoDetails/videoMetrics';
import StreamInfo from './VideoDetails/components/StreamInfo';
import PlaybackMetrics from './VideoDetails/components/PlaybackMetrics';
import URLsSection from './VideoDetails/components/URLsSection';
import EmbeddedCodeSection from './VideoDetails/components/EmbeddedCodeSection';
import CaptionEditor from './VideoDetails/components/CaptionEditor';
import DashPlayer from './VideoDetails/components/DashPlayer';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const VideoDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [jobData, setJobData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const [selectedCaptionFormat, setSelectedCaptionFormat] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [captionUrl, setCaptionUrl] = useState('');
    const [captionText, setCaptionText] = useState('');
    const [loadingCaption, setLoadingCaption] = useState(false);
    const [isEditingCaption, setIsEditingCaption] = useState(false);
    const [savingCaption, setSavingCaption] = useState(false);
    const [captionCacheBuster, setCaptionCacheBuster] = useState(Date.now());
    const [convertedCaptionUrls, setConvertedCaptionUrls] = useState({}); // Store converted SRT to VTT blob URLs
    
    // Video metrics state
    const [streamInfo, setStreamInfo] = useState({
        availableBitrates: '–',
        availableResolutions: '–',
        currentResolution: '–',
        segmentLength: '–',
        fps: '–',
        buffer: '–'
    });
    
    const [playbackMetrics, setPlaybackMetrics] = useState({
        bandwidth: [],
        currentBitrate: [],
        frameRate: []
    });
    
    const [chartLabels, setChartLabels] = useState([]);
    const playerRef = useRef(null);
    const metricsIntervalRef = useRef(null);
    const lastFramesRef = useRef(0);
    const lastTimeRef = useRef(performance.now());
    const hlsInstanceRef = useRef(null);
    const dashPlayerRef = useRef(null);
    
    // Make dashjs available globally for ReactPlayer and configure it to allow multiplexed representations
    // This must run immediately, not in useEffect, to patch dash.js before ReactPlayer uses it
    if (typeof window !== 'undefined') {
        if (!window.dashjs) {
            window.dashjs = dashjs;
        }
        
        // Configure dash.js to allow multiplexed representations
        // This fixes the "Multiplexed representations are intentionally not supported" error (code 34)
        if (window.dashjs && !window.__dashjsPatched) {
            window.__dashjsPatched = true; // Prevent multiple patches
            
            // The error is emitted as an event through EventBus
            // We need to patch the EventBus or ErrorHandler to intercept error code 34
            
            // Try to access EventBus through dependencies
            if (window.dashjs.dependencies) {
                const dependencies = window.dashjs.dependencies;
                
                // Patch Debug logger to suppress error code 34 messages
                // The error is logged by Debug.js before reaching error handlers
                // Try multiple approaches to patch Debug
                if (dependencies.Debug) {
                    // Patch Debug class static methods if they exist
                    if (dependencies.Debug.log && !dependencies.Debug.__logPatched) {
                        const originalDebugLog = dependencies.Debug.log;
                        dependencies.Debug.log = function() {
                            const args = Array.from(arguments);
                            const message = args.join(' ');
                            if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                                return; // Suppress
                            }
                            return originalDebugLog.apply(this, arguments);
                        };
                        dependencies.Debug.__logPatched = true;
                    }
                    
                    // Patch Debug prototype methods
                    if (dependencies.Debug.prototype && !dependencies.Debug.prototype.__patchedForCode34) {
                        const DebugPrototype = dependencies.Debug.prototype;
                        
                        // Patch all methods that might log
                        const methodsToPatch = ['log', 'warn', 'error', 'info', 'debug'];
                        methodsToPatch.forEach(methodName => {
                            if (DebugPrototype[methodName]) {
                                const originalMethod = DebugPrototype[methodName];
                                DebugPrototype[methodName] = function() {
                                    const args = Array.from(arguments);
                                    const message = args.join(' ');
                                    if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                                        return; // Suppress
                                    }
                                    return originalMethod.apply(this, arguments);
                                };
                            }
                        });
                        
                        DebugPrototype.__patchedForCode34 = true;
                    }
                    
                    // Also try to patch the Debug instance methods if Debug is a factory
                    if (typeof dependencies.Debug === 'function') {
                        const DebugFactory = dependencies.Debug;
                        if (!DebugFactory.__patchedForCode34) {
                            dependencies.Debug = function() {
                                const debugInstance = DebugFactory.apply(this, arguments);
                                if (debugInstance && debugInstance.log) {
                                    const originalLog = debugInstance.log;
                                    debugInstance.log = function() {
                                        const args = Array.from(arguments);
                                        const message = args.join(' ');
                                        if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                                            return;
                                        }
                                        return originalLog.apply(this, arguments);
                                    };
                                }
                                return debugInstance;
                            };
                            // Copy static properties
                            Object.keys(DebugFactory).forEach(key => {
                                dependencies.Debug[key] = DebugFactory[key];
                            });
                            DebugFactory.__patchedForCode34 = true;
                        }
                    }
                }
                
                // As a last resort, patch console methods to filter out error code 34 messages
                // This is more aggressive but will catch all console logs
                if (!window.__consolePatchedForCode34) {
                    const originalConsoleError = console.error;
                    const originalConsoleWarn = console.warn;
                    const originalConsoleLog = console.log;
                    const originalConsoleInfo = console.info;
                    const originalConsoleDebug = console.debug;
                    
                    const shouldSuppress = function() {
                        const args = Array.from(arguments);
                        const message = args.join(' ');
                        return message && (
                            message.includes('Multiplexed representations') || 
                            message.includes('code 34') ||
                            message.includes('[Stream] Multiplexed') ||
                            (message.includes('[Stream]') && message.includes('not supported') && message.includes('DASH-AVC'))
                        );
                    };
                    
                    console.error = function() {
                        if (shouldSuppress.apply(this, arguments)) {
                            return; // Suppress
                        }
                        return originalConsoleError.apply(console, arguments);
                    };
                    
                    console.warn = function() {
                        if (shouldSuppress.apply(this, arguments)) {
                            return; // Suppress
                        }
                        return originalConsoleWarn.apply(console, arguments);
                    };
                    
                    console.log = function() {
                        if (shouldSuppress.apply(this, arguments)) {
                            return; // Suppress
                        }
                        return originalConsoleLog.apply(console, arguments);
                    };
                    
                    console.info = function() {
                        if (shouldSuppress.apply(this, arguments)) {
                            return; // Suppress
                        }
                        return originalConsoleInfo.apply(console, arguments);
                    };
                    
                    console.debug = function() {
                        if (shouldSuppress.apply(this, arguments)) {
                            return; // Suppress
                        }
                        return originalConsoleDebug.apply(console, arguments);
                    };
                    
                    window.__consolePatchedForCode34 = true;
                    console.log('Console methods patched to suppress multiplexed representation errors');
                }
                
                // Patch Stream class to suppress error code 34 at the source
                // The error "[Stream] Multiplexed representations are intentionally not supported" 
                // is thrown from the Stream class, so we need to patch it directly
                if (dependencies.Stream && dependencies.Stream.prototype) {
                    const StreamPrototype = dependencies.Stream.prototype;
                    if (!StreamPrototype.__patchedForCode34) {
                        // Patch the activate method which is where the error is likely thrown
                        const originalActivate = StreamPrototype.activate;
                        if (originalActivate) {
                            StreamPrototype.activate = function() {
                                try {
                                    return originalActivate.apply(this, arguments);
                                } catch (error) {
                                    // Suppress error code 34
                                    if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                                        // Suppress the error - don't log or throw
                                        return;
                                    }
                                    // Re-throw other errors
                                    throw error;
                                }
                            };
                        }
                        
                        // Patch all methods in Stream class to catch error code 34
                        // The error might be thrown from any method (like the 'K' function at Stream.js:437)
                        const streamMethods = Object.getOwnPropertyNames(StreamPrototype);
                        for (const methodName of streamMethods) {
                            if (typeof StreamPrototype[methodName] === 'function' && methodName !== 'activate') {
                                const originalMethod = StreamPrototype[methodName];
                                StreamPrototype[methodName] = function() {
                                    try {
                                        return originalMethod.apply(this, arguments);
                                    } catch (error) {
                                        if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                                            // Suppress error code 34
                                            return;
                                        }
                                        throw error;
                                    }
                                };
                            }
                        }
                        
                        StreamPrototype.__patchedForCode34 = true;
                        console.log('Stream class patched to suppress error code 34');
                    }
                }
                
                // Patch EventBus to filter out error code 34
                if (dependencies.EventBus && dependencies.EventBus.prototype) {
                    const EventBusPrototype = dependencies.EventBus.prototype;
                    const originalTrigger = EventBusPrototype.trigger;
                    if (originalTrigger && !EventBusPrototype.__patchedForCode34) {
                        EventBusPrototype.trigger = function(event, data) {
                            // If this is an error event with code 34, suppress it
                            if (event === 'error' && data && data.error && data.error.code === 34) {
                                console.warn('Multiplexed representation error (code 34) suppressed via EventBus');
                                // Don't trigger the error event - suppress it
                                return;
                            }
                            return originalTrigger.apply(this, arguments);
                        };
                        EventBusPrototype.__patchedForCode34 = true;
                        console.log('EventBus patched to suppress error code 34');
                    }
                }
                
                // Patch ErrorHandler if available
                if (dependencies.ErrorHandler && dependencies.ErrorHandler.prototype) {
                    const ErrorHandlerPrototype = dependencies.ErrorHandler.prototype;
                    const originalError = ErrorHandlerPrototype.error;
                    if (originalError && !ErrorHandlerPrototype.__patchedForCode34) {
                        ErrorHandlerPrototype.error = function(error) {
                            // If error is code 34, suppress it
                            if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                                console.warn('Multiplexed representation error (code 34) suppressed via ErrorHandler');
                                return; // Don't emit the error
                            }
                            return originalError.apply(this, arguments);
                        };
                        ErrorHandlerPrototype.__patchedForCode34 = true;
                        console.log('ErrorHandler patched to suppress error code 34');
                    }
                }
            }
            
            // Patch MediaPlayer to configure it and intercept errors
            if (window.dashjs.MediaPlayer) {
                const originalCreate = window.dashjs.MediaPlayer.prototype.create;
                window.dashjs.MediaPlayer.prototype.create = function() {
                    const player = originalCreate.apply(this, arguments);
                    
                    // Intercept error events before they reach ReactPlayer
                    if (player && player.on) {
                        const originalOn = player.on.bind(player);
                        player.on = function(event, listener) {
                            if (event === 'error' && listener) {
                                const wrappedListener = function(errorEvent) {
                                    // Suppress error code 34
                                    if (errorEvent && errorEvent.error && errorEvent.error.code === 34) {
                                        console.warn('Multiplexed representation error (code 34) intercepted and suppressed in MediaPlayer');
                                        return; // Don't call the listener
                                    }
                                    if (listener) {
                                        listener.call(this, errorEvent);
                                    }
                                };
                                return originalOn(event, wrappedListener);
                            }
                            return originalOn(event, listener);
                        };
                    }
                    
                    // Configure player settings to be more permissive
                    if (player && player.updateSettings) {
                        try {
                            player.updateSettings({
                                streaming: {
                                    capabilities: {
                                        filterUnsupportedEssentialProperties: false,
                                        filterUnsupportedAdaptationSetProperties: false
                                    }
                                }
                            });
                        } catch (e) {
                            console.warn('Could not configure DASH player settings:', e);
                        }
                    }
                    
                    return player;
                };
                // console.log('MediaPlayer.create patched to suppress error code 34');
            }
            
            // console.log('dash.js configured to allow multiplexed representations');
        }
    }

    useEffect(() => {
        const fetchJobDetails = async () => {
            if (!id) {
                toast.error('Job ID is missing');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await VideoProcessorAPI.fetchJobDetails(id);
                // console.log("response", response);
                // Handle response - it could be wrapped in result or data property
                const job = response?.result?.job;
                setJobData(job);
            } catch (error) {
                console.error('Error fetching job details:', error);
                toast.error(error?.message || 'Failed to fetch job details');
            } finally {
                setLoading(false);
            }
        };

        fetchJobDetails();
    }, [id]);

    // Fetch caption when format and language are selected
    useEffect(() => {
        const fetchCaption = async () => {
            if (!selectedCaptionFormat || !selectedLanguage || !jobData) {
                setCaptionUrl('');
                setCaptionText('');
                return;
            }

            try {
                setLoadingCaption(true);
                // Extract caption arrays from jobData
                const srtCaptions = jobData?.outputs?.srt_captions || [];
                const vttCaptions = jobData?.outputs?.vtt_captions || [];
                
                // Find the matching caption URL
                const captionArray = selectedCaptionFormat === 'srt' ? srtCaptions : vttCaptions;
                const caption = captionArray.find(c => c.language === selectedLanguage);
                
                if (caption && caption.url) {
                    setCaptionUrl(caption.url);
                    // Fetch the caption content
                    const response = await fetch(caption.url);
                    if (response.ok) {
                        const text = await response.text();
                        setCaptionText(text);
                    } else {
                        setCaptionText('');
                        toast.error('Failed to load caption content');
                    }
                } else {
                    setCaptionUrl('');
                    setCaptionText('');
                }
            } catch (error) {
                console.error('Error fetching caption:', error);
                setCaptionText('');
                toast.error('Failed to fetch caption');
            } finally {
                setLoadingCaption(false);
            }
        };

        fetchCaption();
    }, [selectedCaptionFormat, selectedLanguage, jobData]);

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);
        toast.success('URL copied to clipboard!');
    };

    // Set default values when captions are available (must be before conditional returns)
    // Prioritize WebVTT over SRT if both are available
    useEffect(() => {
        if (jobData && !selectedCaptionFormat && !selectedLanguage) {
            const srtCaptions = jobData?.outputs?.srt_captions || [];
            const vttCaptions = jobData?.outputs?.vtt_captions || [];
            
            // Prefer WebVTT over SRT if available
            if (vttCaptions.length > 0) {
                setSelectedCaptionFormat('webvtt');
                setSelectedLanguage(vttCaptions[0].language);
            } else if (srtCaptions.length > 0) {
                setSelectedCaptionFormat('srt');
                setSelectedLanguage(srtCaptions[0].language);
            }
        }
    }, [jobData, selectedCaptionFormat, selectedLanguage]);

    // Extract URLs from job data based on API response structure (before conditional returns)
    const fileUrl = jobData?.file_url || '';
    const hlsUrl = jobData?.outputs?.hls_master_url || '';
    const dashUrl = jobData?.outputs?.dash_master_url || '';
    const thumbnailUrl = jobData?.outputs?.thumbnail_url || '';
    
    // Extract caption URLs
    const srtCaptions = jobData?.outputs?.srt_captions || [];
    const vttCaptions = jobData?.outputs?.vtt_captions || [];
    // Add type property to distinguish SRT from VTT captions
    const allCaptions = [
        ...srtCaptions.map(caption => ({ ...caption, type: 'srt' })),
        ...vttCaptions.map(caption => ({ ...caption, type: 'vtt' }))
    ];
    
    // Generate iframe code for embedding (returns object with display and actual code)
    const embeddedCodeObj = generateIframeCode(hlsUrl, dashUrl, fileUrl, srtCaptions, vttCaptions);
    const embeddedCode = embeddedCodeObj?.display || embeddedCodeObj?.actual || '';
    const embeddedCodeActual = embeddedCodeObj?.actual || embeddedCodeObj?.display || '';
    
    // Extract available languages from captions
    const availableLanguages = [...new Set([
        ...srtCaptions.map(c => c.language),
        ...vttCaptions.map(c => c.language)
    ])];
    
    // Determine available caption formats
    const hasSRT = srtCaptions.length > 0;
    const hasVTT = vttCaptions.length > 0;
    
    // Use selected URL if available, otherwise use priority: HLS > DASH > file_url
    const videoUrl = selectedVideoUrl || hlsUrl || dashUrl || fileUrl;
    const videoFormat = selectedVideoUrl === hlsUrl ? 'HLS' : selectedVideoUrl === dashUrl ? 'DASH' : 
                       hlsUrl ? 'HLS' : dashUrl ? 'DASH' : 'Direct';

    const handlePlay = (url) => {
        setSelectedVideoUrl(url);
    };


    // Convert SRT captions to VTT format for ReactPlayer
    // This hook must be before conditional returns to maintain hook order
    useEffect(() => {
        if (!srtCaptions || srtCaptions.length === 0) {
            setConvertedCaptionUrls({});
            return;
        }

        let isMounted = true;
        
        const convertSrtCaptions = async () => {
            // Cleanup previous blob URLs
            setConvertedCaptionUrls(prev => {
                Object.values(prev).forEach(url => {
                    if (url && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                });
                return {};
            });

            const converted = {};
            for (const caption of srtCaptions) {
                if (!isMounted) break;
                try {
                    const response = await fetch(`${caption.url}?t=${captionCacheBuster}`);
                    if (response.ok && isMounted) {
                        const srtText = await response.text();
                        const vttText = srtToVtt(srtText);
                        const blob = new Blob([vttText], { type: 'text/vtt' });
                        const blobUrl = URL.createObjectURL(blob);
                        converted[caption.url] = blobUrl;
                    }
                } catch (error) {
                    console.error(`Error converting SRT caption ${caption.url}:`, error);
                }
            }
            
            if (isMounted) {
                setConvertedCaptionUrls(converted);
            } else {
                // Cleanup if component unmounted during conversion
                Object.values(converted).forEach(url => {
                    if (url && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                });
            }
        };

        convertSrtCaptions();

        // Cleanup blob URLs when component unmounts or dependencies change
        return () => {
            isMounted = false;
            setConvertedCaptionUrls(prev => {
                Object.values(prev).forEach(url => {
                    if (url && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                });
                return {};
            });
        };
    }, [srtCaptions, captionCacheBuster]);

    // Function to update video metrics
    const updateMetrics = useCallback(
        createUpdateMetrics(
            playerRef,
            hlsInstanceRef,
            dashPlayerRef,
            lastFramesRef,
            lastTimeRef,
            selectedVideoUrl,
            hlsUrl,
            dashUrl,
            fileUrl,
            setStreamInfo,
            setPlaybackMetrics,
            setChartLabels
        ),
        [selectedVideoUrl, hlsUrl, dashUrl, fileUrl]
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (metricsIntervalRef.current) {
                clearInterval(metricsIntervalRef.current);
            }
        };
    }, []);

    document.title = "Media Pulse | Video Processor";
    
    if (loading) {
        return (
            <div className='page-content'>
                <Container fluid>
                    <BreadCrumb title="Video Details" pageTitle="Video Processor" />
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading job details...</p>
                    </div>
                </Container>
            </div>
        );
    }

    if (!jobData) {
        return (
            <div className='page-content'>
                <Container fluid>
                    <BreadCrumb title="Video Details" pageTitle="Video Processor" />
                    <div className="text-center py-5">
                        <p>Job not found or failed to load.</p>
                    </div>
                </Container>
            </div>
        );
    }


    const handleEditCaption = () => {
        setIsEditingCaption(true);
    };

    const handleSaveCaption = async () => {
        if (!captionUrl || !captionText) {
            toast.error('Caption URL or text is missing');
            return;
        }

        try {
            setSavingCaption(true);
            const user = await getCurrentUser();
            
            // Extract S3 path from caption URL
            const s3Path = extractS3PathFromUrl(captionUrl);
            if (!s3Path) {
                toast.error('Could not extract S3 path from caption URL');
                return;
            }

            // Determine content type based on format
            const contentType = selectedCaptionFormat === 'srt' 
                ? 'text/srt' 
                : 'text/vtt';

            // Create a Blob from the caption text
            const blob = new Blob([captionText], { type: contentType });

            // Upload to S3 with the same path (replaces existing file)
            await uploadData({
                path: s3Path,
                data: blob,
                options: {
                    contentType: contentType,
                },
            }).result;

            toast.success('Caption saved successfully!');
            setIsEditingCaption(false);
            
            // Update cache buster to force video player to reload captions
            const newCacheBuster = Date.now();
            setCaptionCacheBuster(newCacheBuster);
            
            // Re-fetch the caption with cache-busting to get updated content
            if (captionUrl) {
                const cacheBuster = `?t=${newCacheBuster}`;
                const response = await fetch(captionUrl + cacheBuster);
                if (response.ok) {
                    const text = await response.text();
                    setCaptionText(text);
                }
            }
        } catch (error) {
            console.error('Error saving caption:', error);
            toast.error(error?.message || 'Failed to save caption');
        } finally {
            setSavingCaption(false);
        }
    };

    const handleCancelEdit = () => {
        // Re-fetch the original caption text
        if (captionUrl) {
            fetch(captionUrl)
                .then(response => response.text())
                .then(text => {
                    setCaptionText(text);
                    setIsEditingCaption(false);
                })
                .catch(error => {
                    console.error('Error fetching original caption:', error);
                    setIsEditingCaption(false);
                });
        } else {
            setIsEditingCaption(false);
        }
    };

    const handleViewConfiguration = () => {
        // Parse configuration from API response
        const config = jobData?.configuration || {};
        const preprocessVideo = config?.PreprocessVideo || {};
        const thumbnailConfig = preprocessVideo?.Thumbnail || {};
        const transcodeVideo = config?.AnalyzeVideo?.TranscodeVideo || {};
        const transformText = config?.TransformText || {};
        const translateCaptions = config?.Translate?.TranslateWebCaptions || {};
        const webCaptions = config?.WebCaptions?.WebCaptions || {};

        // Extract caption format - check which is enabled (prioritize VTT if both are enabled)
        let captionFormat = 'webvtt';
        const srtEnabled = transformText?.WebToSRTCaptions?.Enabled || false;
        const vttEnabled = transformText?.WebToVTTCaptions?.Enabled || false;
        
        if (srtEnabled && !vttEnabled) {
            captionFormat = 'srt';
        } else if (vttEnabled) {
            captionFormat = 'webvtt';
        }

        // Extract languages from captions
        const captionLanguages = [
            ...(transformText?.WebToSRTCaptions?.TargetLanguageCodes || []),
            ...(transformText?.WebToVTTCaptions?.TargetLanguageCodes || [])
        ];
        const uniqueLanguages = [...new Set(captionLanguages)];

        // Extract HLS and DASH resolutions from CustomSettings
        const customSettings = transcodeVideo?.CustomSettings || {};
        const hlsResolutions = customSettings?.HLS || [];
        const dashResolutions = customSettings?.DASH || [];

        // Format jobData to match Summary page structure
        const summaryData = {
            isViewConfiguration: true, // Flag to indicate this is viewing configuration, not creating new job
            jobName: jobData?.filename || 'N/A',
            workflow: 'Transcode', // Default workflow type
            video: {
                filename: jobData?.filename || '',
                file_id: jobData?.file_id || '',
                cdn_url: jobData?.file_url || '',
            },
            hlsResolutions: hlsResolutions,
            dashResolutions: dashResolutions,
            thumbnailSettings: {
                width: '', // Not available in API response
                height: '', // Not available in API response
                maxCapture: '', // Not available in API response
                enabled: thumbnailConfig?.Enabled || false,
                thumbnailPosition: thumbnailConfig?.ThumbnailPosition ||  '10', 
            },
            overlaySettings: {
                offsetTop: '', // Not available in API response
                offsetLeft: '', // Not available in API response
                width: '', // Not available in API response
                height: '', // Not available in API response
                startTime: '', // Not available in API response
                enabled: false, // Overlay image not available in current API response
            },
            captionsSettings: {
                enableCaptions: config?.AnalyzeVideo?.TranscribeVideo?.Enabled || false,
                enableTranslation: translateCaptions?.Enabled || false,
                captionFormat: captionFormat,
                languages: uniqueLanguages,
            },
        };

        navigate('/video-processor/video-setting-trans/summary', { state: summaryData });
    };

    return (
        <div className='page-content'>
            <Container fluid>
                <BreadCrumb title="Video Details" pageTitle="Video Processor" />
                <Row className='mb-1'>
                    <Col lg={12}>

                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <CardTitle tag="h5" className="mb-0">Job details</CardTitle>
                                    <Button 
                                        style={{ color: 'white', background: '#50c3e6', border: '#50c3e6' }}
                                        size="sm"
                                        onClick={handleViewConfiguration}
                                    >
                                        View Configuration
                                    </Button>
                                </div>
                                <hr />

                                <div className="card-text">
                                    <p><strong>Job ID:</strong> {jobData.job_uuid || jobData.job_id || id}</p>
                                    <p><strong>Job Name:</strong> {jobData.filename || jobData.job_name || 'N/A'}</p>
                                    <p><strong>Status:</strong> {jobData.status || 'N/A'}</p>
                                    <p><strong>Created At:</strong> {jobData.created_at || 'N/A'}</p>
                                    <p><strong>Updated At:</strong> {jobData.updated_at || 'N/A'}</p>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                </Row>
                <Row>
                    <Col lg={6}>
                        <Card className="mb-4">
                            <CardBody>
                                <CardTitle tag="h5" style={{ color: "#6691e7" }}>Video Player</CardTitle>
                                <hr />
                                {videoUrl ? (
                                    <div style={{ width: '100%', marginTop: '1rem' }}>
                                        <div className="mb-2">
                                            <small className="text-muted">
                                                Format: <strong>{videoFormat}</strong>
                                                {hlsUrl && <span className="ms-2 badge bg-success">Adaptive Streaming</span>}
                                                {dashUrl && !hlsUrl && <span className="ms-2 badge bg-info">Adaptive Streaming</span>}
                                            </small>
                                            {videoFormat === 'DASH' && !hlsUrl && (
                                                <div className="alert alert-warning mt-2 mb-0" role="alert">
                                                    <small>
                                                        <strong>Note:</strong> Some DASH videos with multiplexed representations may not play in all browsers. 
                                                        If playback issues occur, please use the HLS format if available, or contact support.
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ position: 'relative' }}>
                                            {false && videoFormat === 'DASH' ? (
                                                // Disabled: Using ReactPlayer instead for better compatibility
                                                // Use direct dash.js player for DASH videos to avoid error code 34
                                                <DashPlayer
                                                    ref={playerRef}
                                                    url={videoUrl}
                                                    onReady={(player) => {
                                                        console.log('DASH player ready (direct dash.js)');
                                                        // Store player reference for metrics
                                                        dashPlayerRef.current = player?.player?.dashjs;
                                                        
                                                        // Try to capture instances for metrics
                                                        try {
                                                            const internalPlayer = player.getInternalPlayer();
                                                            const videoElement = internalPlayer?.tagName === 'VIDEO' 
                                                                ? internalPlayer 
                                                                : internalPlayer?.querySelector?.('video');
                                                            
                                                            if (videoElement && videoElement.__dashjs) {
                                                                dashPlayerRef.current = videoElement.__dashjs;
                                                                console.log('DASH player captured (direct):', dashPlayerRef.current);
                                                            }
                                                        } catch (error) {
                                                            console.error('Error capturing DASH player:', error);
                                                        }
                                                    }}
                                                    onError={(error) => {
                                                        console.error('DASH player error (direct):', error);
                                                        // Error code 34 is already suppressed in DashPlayer component
                                                        if (error?.error?.code !== 34) {
                                                            toast.error('Error playing DASH video');
                                                        }
                                                    }}
                                                    onPlay={() => {
                                                        // Start metrics collection
                                                        if (metricsIntervalRef.current) {
                                                            clearInterval(metricsIntervalRef.current);
                                                        }
                                                        metricsIntervalRef.current = setInterval(() => {
                                                            updateMetrics();
                                                        }, 2000);
                                                    }}
                                                    onPause={() => {
                                                        if (metricsIntervalRef.current) {
                                                            clearInterval(metricsIntervalRef.current);
                                                            metricsIntervalRef.current = null;
                                                        }
                                                    }}
                                                    onEnded={() => {
                                                        if (metricsIntervalRef.current) {
                                                            clearInterval(metricsIntervalRef.current);
                                                            metricsIntervalRef.current = null;
                                                        }
                                                    }}
                                                    tracks={allCaptions
                                                        .filter(caption => caption.type === 'vtt')
                                                        .map((caption, index) => {
                                                            const captionUrl = `${caption.url}?t=${captionCacheBuster}`;
                                                            return {
                                                                kind: 'subtitles',
                                                                src: captionUrl,
                                                                srcLang: caption.language,
                                                                label: languageMap[caption.language] || caption.language.toUpperCase(),
                                                                default: index === 0
                                                            };
                                                        })}
                                                />
                                            ) : (
                                            <ReactPlayer
                                                    ref={playerRef}
                                                    key={`${videoUrl}-${captionCacheBuster}-${Object.keys(convertedCaptionUrls).length}`} // Force re-render when captions are updated or converted
                                                url={videoUrl}
                                                controls
                                                width="100%"
                                                height="auto"
                                                playing={false}
                                                onError={(error) => {
                                                    console.error('ReactPlayer error:', error);
                                                    console.error('Video URL:', videoUrl);
                                                    console.error('Video Format:', videoFormat);
                                                    
                                                    if (videoFormat === 'DASH') {
                                                        // Check if this is error code 34 (multiplexed representations)
                                                        const errorCode = error?.error?.code || error?.code;
                                                        if (errorCode === 34) {
                                                            console.warn('Multiplexed representation error (code 34) detected. Suppressing error and allowing playback to continue...');
                                                            
                                                            // The error has been suppressed by our patches, but ReactPlayer might have already caught it
                                                            // Try to force the player to continue by reloading the source
                                                            try {
                                                                const player = playerRef.current;
                                                                if (player) {
                                                                    // Force reload the video source
                                                                    setTimeout(() => {
                                                                        if (playerRef.current) {
                                                                            try {
                                                                                // Get the internal player and try to reload
                                                                                const internalPlayer = playerRef.current.getInternalPlayer();
                                                                                const videoElement = internalPlayer?.tagName === 'VIDEO' 
                                                                                    ? internalPlayer 
                                                                                    : internalPlayer?.querySelector?.('video');
                                                                                
                                                                                if (videoElement) {
                                                                                    // Try to reload the video
                                                                                    videoElement.load();
                                                                                    console.log('Attempting to reload DASH video after suppressing error code 34');
                                                                                }
                                                                            } catch (reloadError) {
                                                                                console.warn('Could not reload video:', reloadError);
                                                                            }
                                                                        }
                                                                    }, 500);
                                                                }
                                                            } catch (reconfigError) {
                                                                console.error('Failed to handle error code 34:', reconfigError);
                                                            }
                                                            
                                                            // Don't show error toast - the error is suppressed and playback should continue
                                                            console.log('Error code 34 suppressed - playback should continue');
                                                            return;
                                                        }
                                                        
                                                        console.error('DASH playback error details:', {
                                                            url: videoUrl,
                                                            hasDashjs: typeof window.dashjs !== 'undefined',
                                                            errorType: error?.type,
                                                            errorMessage: error?.message,
                                                            errorCode: errorCode,
                                                            errorDetails: error
                                                        });
                                                        toast.error('Error playing DASH video. Please check: 1) URL is HTTPS, 2) CORS is enabled on server, 3) .mpd file is accessible.');
                                                    }
                                                }}
                                                onReady={(player) => {
                                                    // console.log('Video player ready');
                                                    // console.log('Video Format:', videoFormat);
                                                    // console.log('Video URL:', videoUrl);
                                                    
                                                    // For DASH, ensure dash.js is available and configure it
                                                    if (videoFormat === 'DASH') {
                                                        if (typeof window.dashjs === 'undefined') {
                                                            console.warn('dash.js is not loaded. DASH playback may not work.');
                                                            toast.warning('dash.js library not detected. DASH playback may fail.');
                                                        } else {
                                                            // console.log('dash.js is available:', window.dashjs);
                                                            
                                                            // Configure dash.js to allow multiplexed representations
                                                            // This fixes the "Multiplexed representations are intentionally not supported" error
                                                            try {
                                                                const internalPlayer = player.getInternalPlayer();
                                                                const videoElement = internalPlayer?.tagName === 'VIDEO' 
                                                                    ? internalPlayer 
                                                                    : internalPlayer?.querySelector?.('video');
                                                                
                                                                if (videoElement && videoElement.__dashjs) {
                                                                    const dashPlayer = videoElement.__dashjs;
                                                                    // Set configuration to allow multiplexed representations
                                                                    if (dashPlayer.updateSettings) {
                                                                        dashPlayer.updateSettings({
                                                                            streaming: {
                                                                                capabilities: {
                                                                                    filterUnsupportedEssentialProperties: false,
                                                                                    filterUnsupportedAdaptationSetProperties: false
                                                                                }
                                                                            }
                                                                        });
                                                                        // console.log('DASH player configured to allow multiplexed representations');
                                                                    }
                                                                }
                                                            } catch (configError) {
                                                                console.warn('Could not configure DASH player for multiplexed representations:', configError);
                                                            }
                                                        }
                                                    }
                                                    
                                                    // Try to capture hls.js or dash.js instances
                                                    try {
                                                        const internalPlayer = player.getInternalPlayer();
                                                        const videoElement = internalPlayer?.tagName === 'VIDEO' 
                                                            ? internalPlayer 
                                                            : internalPlayer?.querySelector?.('video');
                                                        
                                                        if (videoElement) {
                                                            // For HLS
                                                            if (window.Hls && videoElement.__hls) {
                                                                hlsInstanceRef.current = videoElement.__hls;
                                                                // console.log('HLS instance captured:', hlsInstanceRef.current);
                                                            }
                                                            // For DASH
                                                            if (window.dashjs && videoElement.__dashjs) {
                                                                dashPlayerRef.current = videoElement.__dashjs;
                                                                // console.log('DASH player captured:', dashPlayerRef.current);
                                                            }
                                                            
                                                            // Also check player's internal state
                                                            if (player.player) {
                                                                if (player.player.hls) {
                                                                    hlsInstanceRef.current = player.player.hls;
                                                                }
                                                                if (player.player.dashjs) {
                                                                    dashPlayerRef.current = player.player.dashjs;
                                                                }
                                                            }
                                                            
                                                            // Log video element info for DASH debugging
                                                            if (videoFormat === 'DASH') {
                                                                // console.log('Video element for DASH:', {
                                                                //     src: videoElement.src,
                                                                //     currentSrc: videoElement.currentSrc,
                                                                //     readyState: videoElement.readyState,
                                                                //     networkState: videoElement.networkState,
                                                                //     error: videoElement.error
                                                                // });
                                                            }
                                                        }
                                                    } catch (error) {
                                                        console.error('Error capturing player instances:', error);
                                                    }
                                                }}
                                                onPlay={() => {
                                                    // Try to capture instances again when video starts playing
                                                    setTimeout(() => {
                                                        try {
                                                            if (!playerRef.current) return;
                                                            
                                                            const player = playerRef.current;
                                                            const internalPlayer = player.getInternalPlayer();
                                                            const videoElement = internalPlayer?.tagName === 'VIDEO' 
                                                                ? internalPlayer 
                                                                : internalPlayer?.querySelector?.('video');
                                                            
                                                            if (videoElement) {
                                                                // Check all possible locations for hls.js
                                                                if (window.Hls) {
                                                                    // Try multiple access methods
                                                                    let hlsInstance = videoElement.__hls || 
                                                                                       videoElement.hls ||
                                                                                       (videoElement.parentElement && videoElement.parentElement.__hls) ||
                                                                                       (internalPlayer && internalPlayer.__hls) ||
                                                                                       (internalPlayer && internalPlayer.hls);
                                                                    
                                                                    // Also check ReactPlayer's internal state
                                                                    if (!hlsInstance && player.player) {
                                                                        hlsInstance = player.player.hls || player.player.__hls;
                                                                    }
                                                                    
                                                                    // Search through all properties recursively
                                                                    if (!hlsInstance) {
                                                                        const searchForHls = (obj, depth = 0) => {
                                                                            if (depth > 3 || !obj) return null;
                                                                            if (obj.levels && obj.loadSource && obj.attachMedia) {
                                                                                return obj; // This looks like an hls.js instance
                                                                            }
                                                                            for (const key in obj) {
                                                                                if (key === 'hls' || key === '__hls') {
                                                                                    return obj[key];
                                                                                }
                                                                                if (typeof obj[key] === 'object' && obj[key] !== null) {
                                                                                    const found = searchForHls(obj[key], depth + 1);
                                                                                    if (found) return found;
                                                                                }
                                                                            }
                                                                            return null;
                                                                        };
                                                                        
                                                                        hlsInstance = searchForHls(player.player) || 
                                                                                     searchForHls(internalPlayer) ||
                                                                                     searchForHls(videoElement);
                                                                    }
                                                                    
                                                                    if (hlsInstance) {
                                                                        hlsInstanceRef.current = hlsInstance;
                                                                        // console.log('HLS instance captured on play:', hlsInstance);
                                                                    } else {
                                                                        // console.log('HLS instance not found. Searching...');
                                                                        // console.log('Player object:', player);
                                                                        // console.log('Player.player:', player.player);
                                                                        // console.log('Internal player:', internalPlayer);
                                                                        // console.log('Video element:', videoElement);
                                                                    }
                                                                }
                                                                
                                                                // Check all possible locations for dash.js
                                                                if (window.dashjs) {
                                                                    let dashPlayer = videoElement.__dashjs || 
                                                                                      videoElement.dashjs ||
                                                                                      (videoElement.parentElement && videoElement.parentElement.__dashjs) ||
                                                                                      (internalPlayer && internalPlayer.__dashjs) ||
                                                                                      (internalPlayer && internalPlayer.dashjs);
                                                                    
                                                                    // Also check ReactPlayer's internal state
                                                                    if (!dashPlayer && player.player) {
                                                                        dashPlayer = player.player.dashjs || player.player.__dashjs;
                                                                    }
                                                                    
                                                                    // Search through all properties recursively
                                                                    if (!dashPlayer) {
                                                                        const searchForDash = (obj, depth = 0) => {
                                                                            if (depth > 3 || !obj) return null;
                                                                            if (obj.getBitrateInfoListFor && obj.getQualityFor) {
                                                                                return obj; // This looks like a dash.js player
                                                                            }
                                                                            for (const key in obj) {
                                                                                if (key === 'dashjs' || key === '__dashjs') {
                                                                                    return obj[key];
                                                                                }
                                                                                if (typeof obj[key] === 'object' && obj[key] !== null) {
                                                                                    const found = searchForDash(obj[key], depth + 1);
                                                                                    if (found) return found;
                                                                                }
                                                                            }
                                                                            return null;
                                                                        };
                                                                        
                                                                        dashPlayer = searchForDash(player.player) || 
                                                                                    searchForDash(internalPlayer) ||
                                                                                    searchForDash(videoElement);
                                                                    }
                                                                    
                                                                    if (dashPlayer) {
                                                                        dashPlayerRef.current = dashPlayer;
                                                                        // console.log('DASH player captured on play:', dashPlayer);
                                                                    } else {
                                                                        // console.log('DASH player not found');
                                                                    }
                                                                }
                                                            }
                                                        } catch (error) {
                                                            console.error('Error capturing instances on play:', error);
                                                        }
                                                    }, 1000);
                                                    
                                                    if (metricsIntervalRef.current) {
                                                        clearInterval(metricsIntervalRef.current);
                                                    }
                                                    metricsIntervalRef.current = setInterval(() => {
                                                        updateMetrics();
                                                    }, 2000);
                                                }}
                                                onPause={() => {
                                                    if (metricsIntervalRef.current) {
                                                        clearInterval(metricsIntervalRef.current);
                                                        metricsIntervalRef.current = null;
                                                    }
                                                }}
                                                onEnded={() => {
                                                    if (metricsIntervalRef.current) {
                                                        clearInterval(metricsIntervalRef.current);
                                                        metricsIntervalRef.current = null;
                                                    }
                                                }}
                                                config={{
                                                    file: {
                                                        attributes: {
                                                            crossOrigin: 'anonymous',
                                                        },
                                                        forceDASH: videoFormat === 'DASH', // Force DASH for .mpd files
                                                        forceHLS: videoFormat === 'HLS', // Force HLS for .m3u8 files
                                                        tracks: allCaptions
                                                            .filter(caption => caption.type === 'vtt') // Only include VTT captions, exclude SRT
                                                            .map((caption, index) => {
                                                                // Use original VTT URL with cache buster
                                                                const captionUrl = `${caption.url}?t=${captionCacheBuster}`;
                                                            
                                                            return {
                                                                kind: 'subtitles',
                                                                src: captionUrl,
                                                                srcLang: caption.language,
                                                                label: languageMap[caption.language] || caption.language.toUpperCase(),
                                                                    default: false, // Will set default below
                                                            };
                                                        })
                                                            .map((track, index) => ({
                                                                ...track,
                                                                default: index === 0 // Set first available caption as default
                                                            }))
                                                    },
                                                    dash: {
                                                        // DASH-specific configuration for AVC/264 and AVC/265 support
                                                        // React-player uses dash.js internally, these settings are passed to dash.js
                                                        streaming: {
                                                            retryAttempts: {
                                                                MediaSegment: 3,
                                                                Fragment: 3,
                                                                Manifest: 3
                                                            },
                                                            lowLatencyEnabled: false,
                                                            bufferToKeep: 20,
                                                            bufferAheadToKeep: 40,
                                                            // Add CORS handling for DASH
                                                            xhrWithCredentials: false,
                                                            // Enable all codecs and allow multiplexed representations
                                                            capabilities: {
                                                                filterUnsupportedEssentialProperties: false,
                                                                filterUnsupportedAdaptationSetProperties: false,
                                                                // Allow multiplexed representations (audio+video in same stream)
                                                                // This is needed for some DASH manifests that don't strictly follow DASH-AVC/264 guidelines
                                                                supportsMultiplexed: true
                                                            }
                                                        },
                                                        // Additional DASH configuration
                                                        abr: {
                                                            ABRStrategy: 'abrBola',
                                                            maxBitrate: {
                                                                audio: -1,
                                                                video: -1
                                                            },
                                                            minBitrate: {
                                                                audio: -1,
                                                                video: -1
                                                            }
                                                        }
                                                    }
                                                }}
                                                onProgress={(state) => {
                                                    // Log progress for debugging DASH playback
                                                    if (videoFormat === 'DASH' && state.loaded > 0) {
                                                        // console.log('DASH playback progress:', state);
                                                    }
                                                }}
                                                onBuffer={(state) => {
                                                    if (videoFormat === 'DASH') {
                                                        // console.log('DASH buffer state:', state);
                                                    }
                                                }}
                                                onBufferEnd={() => {
                                                    if (videoFormat === 'DASH') {
                                                        // console.log('DASH buffer ended');
                                                    }
                                                }}
                                            />
                                            )}
                                        </div>
                                        
                                    </div>
                                ) : (
                                    <p className="card-text text-muted">No video URL available</p>
                                )}
                            </CardBody>
                        </Card>
                        
                        <StreamInfo streamInfo={streamInfo} />
                        <PlaybackMetrics chartLabels={chartLabels} playbackMetrics={playbackMetrics} />
                        <URLsSection 
                            hlsUrl={hlsUrl} 
                            dashUrl={dashUrl} 
                            thumbnailUrl={thumbnailUrl} 
                            onPlay={handlePlay}
                            onCopy={handleCopy}
                        />
                        
                    </Col>
                    <Col lg={6}>
                        <Card className="mb-4">
                            <CardBody>
                                <CardTitle tag="h5" style={{ color: "#6691e7" }}>Thumbnail</CardTitle>
                                <hr />
                                {thumbnailUrl ? (
                                    <div className="text-center">
                                        <img 
                                            src={thumbnailUrl} 
                                            alt="Video Thumbnail" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                height: 'auto',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                        <p className="text-muted mt-2" style={{ display: 'none' }}>
                                            Failed to load thumbnail
                                        </p>
                                    </div>
                                ) : (
                                    <p className="card-text text-muted text-center">No thumbnail available</p>
                                )}
                            </CardBody>
                        </Card>
                        <CaptionEditor
                            selectedCaptionFormat={selectedCaptionFormat}
                            setSelectedCaptionFormat={setSelectedCaptionFormat}
                            selectedLanguage={selectedLanguage}
                            setSelectedLanguage={setSelectedLanguage}
                            captionUrl={captionUrl}
                            captionText={captionText}
                            setCaptionText={setCaptionText}
                            loadingCaption={loadingCaption}
                            isEditingCaption={isEditingCaption}
                            savingCaption={savingCaption}
                            hasVTT={hasVTT}
                            hasSRT={hasSRT}
                            availableLanguages={availableLanguages}
                            onEdit={handleEditCaption}
                            onSave={handleSaveCaption}
                            onCancel={handleCancelEdit}
                        />
                        <EmbeddedCodeSection embeddedCode={embeddedCode} embeddedCodeActual={embeddedCodeActual} />
                    </Col>
                </Row>
                <div className="d-flex justify-content-start mb-3 gap-2">
                    <Button 
                        color="secondary" 
                        onClick={() => navigate('/video-processor')}
                    >
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>
            </Container>

        </div>
    );
};

export default VideoDetails;

